import { Glyph } from '@/components/svgs/Glyph'
import Logo, { LogoColorVariant } from '@/components/svgs/Logo'
import { colors } from '@/constants/colors'
import { Pressable } from '@/ui/core/pressable'
import { cn } from '@/ui/utils'
import { type ReactNode, useEffect } from 'react'
import { View } from 'react-native'
import Animated, {
	Easing,
	ReduceMotion,
	useAnimatedStyle,
	useSharedValue,
	withTiming,
} from 'react-native-reanimated'
import { flowHeaderStyles } from './flow-header.styles'

/**
 * The measure's fill, which runs to where it has got to rather than jumping there. A bar that
 * cuts from one width to the next is a readout; one that travels is the thing the answer moved.
 */
const Measure = ({ percent, className }: { percent: number; className: string }) => {
	const width = useSharedValue(percent)

	useEffect(() => {
		width.value = withTiming(percent, {
			duration: 380,
			easing: Easing.out(Easing.cubic),
			reduceMotion: ReduceMotion.System,
		})
	}, [percent, width])

	const style = useAnimatedStyle(() => ({ width: `${width.value}%` }))

	return <Animated.View className={className} style={style} />
}

type FlowHeaderIconName = 'arrow-back' | 'close'

export const FlowHeaderIcon = ({
	name,
	color = colors.ink,
}: { name: FlowHeaderIconName; color?: string }) =>
	name === 'arrow-back' ? (
		<Glyph name="arrow-light" size={22} color={color} rotate={180} />
	) : (
		// The app's own cross, not a borrowed icon set's: one glyph family, and the two marks in
		// this header are drawn on the same grid at the same weight.
		<Glyph name="x-light" size={22} color={color} />
	)

/**
 * The mark alone: no ring, no fill, drawn thin and in ink.
 *
 * It used to be a 40pt ring in the grey a second line takes, on the argument that chrome should
 * be found when looked for rather than compete with the question. Drawn, the ring was the
 * competing part — a circle is a shape, and the app put one on every screen next to a wordmark
 * that has none. The mark is the whole control now, which is why it is a light cut rather than
 * the heavy one: `arrow` and `x` are drawn to sit on a disc, where a ring and a fill carry them,
 * and standing alone they read as blobs.
 *
 * The target is the same. Hit slop of 16 around a 22pt glyph is the 44 a finger needs, where the
 * ring used to supply it geometrically.
 */
const IconButton = ({
	name,
	label,
	onPress,
	onPhoto = false,
}: {
	name: FlowHeaderIconName
	label: string
	onPress: () => void
	onPhoto?: boolean
}) => (
	<Pressable
		onPress={onPress}
		accessibilityRole="button"
		accessibilityLabel={label}
		hitSlop={16}
		className="h-10 w-10 items-center justify-center"
	>
		<FlowHeaderIcon name={name} color={onPhoto ? colors.white : colors.ink} />
	</Pressable>
)

interface FlowHeaderProps {
	/** Renders the back arrow on the left. */
	onBack?: () => void
	/** Renders the close cross on the right, unless `right` supplies something else. */
	onClose?: () => void
	/** Whatever sits on the left instead of the back arrow: a screen's own quiet status. */
	left?: ReactNode
	/** Whatever sits on the right instead of the cross: the navigation menu, a dialog's close. */
	right?: ReactNode
	/** The wordmark, centred. Off only for the full-screen menu, which is nothing but links. */
	logo?: boolean
	/** How far along a stepped flow is, 0 to 1; draws the coral bar under the row. */
	progress?: number
	/** A strip under the bar, like the deck's mint "you're saving" notice. */
	banner?: ReactNode
	/**
	 * The hairline floor under the chrome, edge to edge.
	 *
	 * On by default, because without it the wordmark floats on the same pink as the page and a
	 * screen has no top. It runs past the page's own margins on purpose: a line that stopped where
	 * the content starts would read as an underline belonging to the heading below it rather than
	 * as a floor under the bar above it.
	 *
	 * Off where something else is already the floor — the stepped measure on a form, the
	 * travelling rule the schedule screen loads with — or where there is no floor to draw, as on
	 * a photograph.
	 */
	rule?: boolean
	/** On a photograph the bar goes clear and the wordmark white. */
	tone?: 'ground' | 'photo'
	className?: string
}

/**
 * The bar at the top of every screen: back on the left, WALLY in the middle, close or menu on
 * the right. The wordmark is positioned absolutely, so however wide the right slot gets (the
 * desktop navigation is far wider than a cross) it stays on the true centre.
 *
 * Carries no safe-area inset of its own; the route's `Screen` supplies the top edge.
 */
export const FlowHeader = ({
	onBack,
	onClose,
	left,
	right,
	logo = true,
	progress,
	banner,
	rule = true,
	tone = 'ground',
	className,
}: FlowHeaderProps) => {
	const styles = flowHeaderStyles()
	const onPhoto = tone === 'photo'
	const percent =
		progress === undefined ? null : Math.round(Math.min(1, Math.max(0, progress)) * 100)

	return (
		<View className={cn('w-full', className)}>
			<View className={cn(styles.bar(), onPhoto && 'bg-transparent')}>
				<View className={cn(styles.side(), 'justify-start')}>
					{left ??
						(onBack && (
							<IconButton
								name="arrow-back"
								label="Back"
								onPress={onBack}
								onPhoto={onPhoto}
							/>
						))}
				</View>
				{logo && (
					<View className={styles.logoWrap()}>
						<Logo
							className={styles.logo()}
							color={onPhoto ? LogoColorVariant.LIGHT : LogoColorVariant.DARK}
						/>
					</View>
				)}
				<View className={cn(styles.side(), 'justify-end')}>
					{right ??
						(onClose && (
							<IconButton
								name="close"
								label="Close"
								onPress={onClose}
								onPhoto={onPhoto}
							/>
						))}
				</View>
			</View>
			{percent !== null && (
				<View className={cn(styles.progressTrack(), onPhoto && 'bg-white/30')}>
					<Measure percent={percent} className={styles.progressFill()} />
				</View>
			)}
			{/* The measure is its own floor, and a photograph has none to draw. */}
			{rule && percent === null && !onPhoto && (
				<View className="h-px w-full" style={{ backgroundColor: colors.ink }} />
			)}
			{banner}
		</View>
	)
}
